import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReAuth } from '@/api/baseApi';
import { organizationsTags } from '@/api/organizations/tags';
import {
  OrganizationShort,
  OrganizationTreeResponse,
  OrgTreeFilter,
  OrgTreeWithFacilitiesFilter,
} from '@/api/organizations/types';
import {
  ORG_BY_ID,
  ORG_BY_ID_BLOCK,
  ORG_BY_ID_UNBLOCK,
  ORG_FACILITY,
  ORG_FACILITY_BY_ID,
  ORG_ID_SET,
  ORG_SHORT,
  ORG_TREE,
  ORG_TREE_WITH_FACILITIES,
  ORGANIZATIONS_API,
} from '@/shared/organizations/api';
import { mapSortToQueryParameter } from '@/utils/utils';

export const organizationsApi = createApi({
  reducerPath: 'organizationsApi',
  baseQuery: baseQueryWithReAuth,
  tagTypes: Object.values(organizationsTags),
  endpoints: (builder) => ({
    getOrganizationsTree: builder.query<OrganizationTreeResponse, OrgTreeFilter | void>({
      query: (payload) => ({
        url: ORG_TREE,
        params: {
          ...payload,
        },
      }),
      keepUnusedDataFor: 60,
      providesTags: [organizationsTags.TREE],
    }),
    getOrganizationById: builder.query<any, { id: string }>({
      queryFn: async (payload, _api, _, baseQuery) => {
        const result = await baseQuery({
          url: ORG_BY_ID(payload.id),
        });
        return { data: result?.data as any };
      },
      providesTags: [organizationsTags.ORGANIZATION],
    }),
    addOrganization: builder.mutation<any, any>({
      query: (payload) => ({
        url: ORGANIZATIONS_API,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [organizationsTags.ORGANIZATIONS, organizationsTags.ORGANIZATION],
    }),
    addFacility: builder.mutation<any, any>({
      query: (payload) => ({
        url: ORG_FACILITY,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [organizationsTags.ORGANIZATION],
    }),
    updateFacility: builder.mutation<any, any>({
      query: (payload) => ({
        url: ORG_FACILITY_BY_ID(payload?.id),
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: [organizationsTags.ORGANIZATION],
    }),
    updateOrganization: builder.mutation<any, any>({
      query: (payload) => ({
        url: ORG_BY_ID(payload?.id),
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: [organizationsTags.ORGANIZATIONS, organizationsTags.ORGANIZATION],
    }),
    blockOrganization: builder.mutation<void, string>({
      query: (id) => ({
        url: ORG_BY_ID_BLOCK(id),
        method: 'POST',
      }),
      invalidatesTags: [organizationsTags.ORGANIZATIONS, organizationsTags.ORGANIZATION],
    }),
    unblockOrganization: builder.mutation<void, string>({
      query: (id) => ({
        url: ORG_BY_ID_UNBLOCK(id),
        method: 'POST',
      }),
      invalidatesTags: [organizationsTags.ORGANIZATIONS, organizationsTags.ORGANIZATION],
    }),
    deleteOrganization: builder.mutation<void, string>({
      query: (id) => ({
        url: ORG_BY_ID(id),
        method: 'DELETE',
      }),
      invalidatesTags: [organizationsTags.ORGANIZATIONS, organizationsTags.ORGANIZATION],
    }),
    deleteFacility: builder.mutation<void, string>({
      query: (id) => ({
        url: ORG_FACILITY_BY_ID(id),
        method: 'DELETE',
      }),
      invalidatesTags: [organizationsTags.ORGANIZATION],
    }),
    getAllOrganizations: builder.query<any, any>({
      queryFn: async (payload, _api, _, baseQuery) => {
        const result = await baseQuery({
          url: ORGANIZATIONS_API,
          params: {
            ...payload?.filters,
            page: payload?.pageable?.page,
            size: payload?.pageable?.size,
            sort: mapSortToQueryParameter(payload?.sort),
          },
        });
        return { data: result?.data as any };
      },
      providesTags: [organizationsTags.ALL_ORGANIZATIONS],
    }),
    getOrgWithFacilities: builder.query<any, OrgTreeWithFacilitiesFilter | void>({
      query: (payload) => ({
        url: ORG_TREE_WITH_FACILITIES,
        method: 'GET',
        params: {
          ...payload,
        },
      }),
      providesTags: [organizationsTags.STRUCTURAL],
    }),
    getOrganizationsShort: builder.query<OrganizationShort[], OrgTreeFilter | void>({
      query: (payload) => ({
        url: ORG_SHORT,
        method: 'GET',
        params: {
          ...payload,
        },
      }),
      providesTags: [organizationsTags.SHORT],
    }),
    getOrganizationIdSet: builder.query<any, string[]>({
      query: (payload) => ({
        url: ORG_ID_SET,
        method: 'POST',
        body: payload,
      }),
    }),
    ancestorByLevel: builder.query<any, string>({
      query: (level) => ({ url: ORG_SHORT, params: { level } }),
    }),
  }),
});

export const {
  useGetOrganizationsTreeQuery,
  useLazyGetOrganizationsTreeQuery,
  useAddOrganizationMutation,
  useAddFacilityMutation,
  useUpdateFacilityMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useBlockOrganizationMutation,
  useUnblockOrganizationMutation,
  useDeleteFacilityMutation,
  useGetOrganizationByIdQuery,
  useGetAllOrganizationsQuery,
  useLazyGetOrgWithFacilitiesQuery,
  useGetOrganizationsShortQuery,
  useGetOrganizationIdSetQuery,
  useLazyGetOrganizationIdSetQuery,
} = organizationsApi;
